"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useSubscription } from "@/features/subscription/queries";
import { CalendarClock, Clock, ClipboardCheck, CreditCard, Sparkles, Wallet, X } from "lucide-react";
import { useConversionSurfaceState } from "@/lib/powerspot/use-conversion-surface-state";
import { topbarPillBaseClasses } from "@/components/layout/balance-button";
import { PowerspotPopover } from "@/components/layout/powerspot-popover";
import { useBalance } from "@/features/subscription/payment-queries";
import { AddBalance as AddBalanceDialog } from "@/features/subscription/components/add-balance";
import { useAppStore } from "@/stores";
import { useAuth } from "@/hooks/use-auth";
import { usePermissions } from "@/hooks/use-permissions";
import { markDismissed } from "@/lib/powerspot/resolver";
import { bookingCalendarTarget, bookingCalendarUrl, withBookingUtms } from "@/lib/utils/calendly";
import { track, trackFunnel } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";

const ACTIVATION_DISMISS_DAYS = 7;

export function PowerspotPill() {
  const { topbar } = useConversionSurfaceState();
  const variant = topbar.variant;

  if (topbar.loading) return null;

  if (variant === "payment_failed") return <PaymentFailedPill />;
  if (variant === "trial_expired") return <TrialExpiredPill />;
  if (variant === "low_balance") return <LowBalancePill />;
  if (variant === "activation") {
    const meta = topbar.activationMeta;
    if (!meta) return null;
    return <ActivationPill completed={meta.completedCount} total={meta.totalSteps} />;
  }
  if (variant === "annual") return <AnnualPill />;
  if (variant === "book_meeting") return <BookMeetingPill />;
  return null;
}

function LowBalancePill() {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data: balanceData } = useBalance();
  const { canManageBilling } = usePermissions();
  const currentBalance = balanceData?.balance ?? 0;

  function handleClick() {
    trackFunnel("powerspot", "low_balance_clicked");
    setDialogOpen(true);
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`${topbarPillBaseClasses} border-warning/40 bg-warning/10 text-warning hover:bg-warning/15`}
        aria-label={t("TOPBAR.POWERSPOT.LOW_BALANCE_ARIA", { defaultValue: "Low credit: add credits" })}
        data-testid="topbar-powerspot-low-balance"
      >
        <Wallet className="h-3.5 w-3.5" aria-hidden="true" />
        <span data-sentry-mask className="hidden xl:inline font-medium">
          ${currentBalance.toFixed(2)}
        </span>
        <span className="hidden xl:inline">· {t("TOPBAR.POWERSPOT.LOW_BALANCE.A", { defaultValue: "Add credit" })}</span>
      </button>
      <AddBalanceDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        source="topbar_powerspot_low_balance"
        canManageBilling={canManageBilling}
      />
    </>
  );
}

function PaymentFailedPill() {
  const { t } = useTranslation();
  const openSubscriptionGate = useAppStore((s) => s.openSubscriptionGate);

  function handleClick() {
    track("subscription", "topbar_powerspot_payment_failed_clicked");
    openSubscriptionGate({
      source: "topbar_powerspot_payment_failed",
      titleKey: "PAYWALL.PAYMENT_FAILED.TITLE",
      descKey: "PAYWALL.PAYMENT_FAILED.DESC",
    });
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`${topbarPillBaseClasses} border-danger/40 bg-danger/10 text-danger hover:bg-danger/15`}
      aria-label={t("TOPBAR.POWERSPOT.PAYMENT_FAILED.ARIA", {
        defaultValue: "Payment failed: update payment method",
      })}
      data-testid="topbar-powerspot-payment-failed"
    >
      <CreditCard className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="hidden xl:inline font-medium">
        {t("TOPBAR.POWERSPOT.PAYMENT_FAILED.LABEL", { defaultValue: "Payment failed" })}
      </span>
    </button>
  );
}

function TrialExpiredPill() {
  const { t } = useTranslation();
  return (
    <Link
      to="/pricing"
      className={`${topbarPillBaseClasses} border-warning/40 bg-warning/10 text-warning hover:bg-warning/15`}
      aria-label={t("TOPBAR.POWERSPOT.TRIAL_EXPIRED.ARIA", {
        defaultValue: "Trial expired: choose a plan",
      })}
      data-testid="topbar-powerspot-trial-expired"
    >
      <Clock className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="hidden xl:inline font-medium">
        {t("TOPBAR.POWERSPOT.TRIAL_EXPIRED.LABEL", { defaultValue: "Trial expired" })}
      </span>
    </Link>
  );
}

function ActivationPill({ completed, total }: { completed: number; total: number }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [locallyDismissed, setLocallyDismissed] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    trackFunnel("activation", "topbar_activation_impression", {
      completed_count: completed,
      total_steps: total,
    });
    pushDataLayer("Activation", "Topbar Activation Impression", `${completed}/${total}`);
    // Mount-only impression, re-fire intentionally suppressed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleOpenChange(next: boolean) {
    setPopoverOpen(next);
    if (!next) {
      // Restore focus to the trigger on close (WCAG 2.4.3 / 2.4.7).
      queueMicrotask(() => triggerRef.current?.focus());
    }
  }

  function handleDismiss(source: "pill" | "popover") {
    if (!user) return;
    markDismissed(user._id, "activation", ACTIVATION_DISMISS_DAYS);
    track("activation", "topbar_activation_dismissed", { source });
    pushDataLayer("Activation", "Topbar Activation Dismissed", source);
    setLocallyDismissed(true);
    setPopoverOpen(false);
  }

  if (locallyDismissed) return null;

  return (
    <>
      <div className="inline-flex shrink-0 items-center gap-0.5">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setPopoverOpen(true)}
          className={`${topbarPillBaseClasses} border-primary/40 bg-primary/10 text-primary hover:bg-primary/15`}
          aria-haspopup="dialog"
          aria-expanded={popoverOpen}
          aria-controls={popoverOpen ? "powerspot-activation-popover" : undefined}
          aria-label={t("TOPBAR.POWERSPOT.ACTIVATION.ARIA", {
            completed,
            total,
            defaultValue: "Setup {{completed}} of {{total}} steps complete",
          })}
          data-testid="topbar-powerspot-activation"
        >
          <ClipboardCheck className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="hidden xl:inline font-medium truncate">
            {t("TOPBAR.POWERSPOT.ACTIVATION.LABEL", {
              completed,
              total,
              defaultValue: "Setup {{completed}}/{{total}}",
            })}
          </span>
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            handleDismiss("pill");
          }}
          className="hidden sm:inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label={t("TOPBAR.POWERSPOT.ACTIVATION.DISMISS_ARIA", {
            defaultValue: "Dismiss activation reminder",
          })}
          data-testid="topbar-powerspot-activation-dismiss"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
      <PowerspotPopover
        open={popoverOpen}
        onOpenChange={handleOpenChange}
        onDismiss={() => handleDismiss("popover")}
      />
    </>
  );
}

function AnnualPill() {
  const { t } = useTranslation();

  return (
    <Link
      to="/pricing?interval=annual"
      onClick={() => trackFunnel("powerspot", "annual_clicked")}
      className={`${topbarPillBaseClasses} border-primary/40 bg-primary/10 text-primary hover:bg-primary/15`}
      aria-label={t("TOPBAR.POWERSPOT.ANNUAL_ARIA", { defaultValue: "Switch to annual billing" })}
      data-testid="topbar-powerspot-annual"
    >
      <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="hidden xl:inline font-medium">
        {t("TOPBAR.POWERSPOT.ANNUAL.A", { defaultValue: "Save with annual" })}
      </span>
    </Link>
  );
}

function BookMeetingPill() {
  const { t } = useTranslation();
  const { data: subscription } = useSubscription();
  const { user } = useAuth();

  const target = bookingCalendarTarget(subscription, user?.isCompanyDomain);
  const calendlyUrl = withBookingUtms(bookingCalendarUrl(target), "topbar_pill", { userId: user?._id });

  useEffect(() => {
    trackFunnel("booking", "cta_shown", { surface: "topbar_pill", target });
    pushDataLayer("Booking", "CTA Shown", "topbar_pill", undefined, { surface: "topbar_pill", target });
  }, []);

  return (
    <a
      href={calendlyUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        trackFunnel("powerspot", "book_meeting_clicked", { target });
        trackFunnel("booking", "cta_clicked", { surface: "topbar_pill", target });
        pushDataLayer("Booking", "CTA Clicked", "topbar_pill", undefined, { surface: "topbar_pill", target });
      }}
      className={`${topbarPillBaseClasses} border-border text-foreground hover:bg-muted`}
      aria-label={t("TOPBAR.POWERSPOT.BOOK_MEETING_ARIA", { defaultValue: "Book a meeting with Speak AI" })}
      data-testid="topbar-powerspot-book-meeting"
    >
      <CalendarClock className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
      <span className="hidden xl:inline font-medium">
        {target === "free-call"
          ? t("TOPBAR.BOOK_CALL_FREE", "Book a free call")
          : t("TOPBAR.POWERSPOT.BOOK_MEETING.A", { defaultValue: "Book a meeting" })}
      </span>
    </a>
  );
}
